/** Intent 2.0 / 3.0 bubble badge — keep in sync with admin/src/utils/intentPipelineBadge.js */

function messageTimings(message) {
  if (!message || typeof message !== 'object') return null;
  return message.timings ?? message.metadata?.timings ?? null;
}

function messageSettings(message) {
  if (!message || typeof message !== 'object') return null;
  const metadata = message.metadata && typeof message.metadata === 'object' ? message.metadata : null;
  return message.settings ?? metadata?.settings ?? null;
}

/** Turn used Intent 3.0 (TypeSafe Jev targeting + disambiguation). */
export function messageUsesIntent30(message) {
  const timings = messageTimings(message);
  if (timings?.jevTargeting != null && typeof timings.jevTargeting === 'object') return true;
  const emb = timings?.intentEmbeddingTiming;
  if (emb?.postIntentDisambiguationSource === 'jev') return true;
  if (emb?.intentPipelineVersion === 3) return true;
  if (timings?.intentPipelineVersion === 3) return true;
  const targetSource = timings?.targetSource;
  if (typeof targetSource === 'string' && targetSource.startsWith('intent30-')) return true;
  const settings = messageSettings(message);
  return settings?.use_intent3 === true || settings?.use_jev_mh_intent === true;
}

/** Turn used embedding intent pipeline (2.0 or 3.0). */
export function messageUsesIntent20(message) {
  if (messageUsesIntent30(message)) return true;
  const timings = messageTimings(message);
  if (timings?.intentEmbeddingTiming != null && typeof timings.intentEmbeddingTiming === 'object') return true;
  const settings = messageSettings(message);
  return settings?.useIntent20 === true;
}

const INTENT_CHIP_H = 26;

/** TypeSafe palette: Ink / Dark Zinc (#18181B) + Mist / Cool Grey (#F4F4F5). */
export const intent30ChipSx = {
  height: INTENT_CHIP_H,
  borderRadius: `${INTENT_CHIP_H / 2}px`,
  maxHeight: INTENT_CHIP_H,
  ml: 2,
  fontSize: '0.62rem',
  fontWeight: 800,
  letterSpacing: '0.06em',
  fontFamily: '"Space Mono", "Roboto Mono", ui-monospace, monospace',
  color: '#F4F4F5',
  cursor: 'default',
  border: '2px solid #F4F4F5',
  background: 'linear-gradient(165deg, #27272a 0%, #18181B 55%, #09090b 100%)',
  boxShadow:
    '0 0 10px rgba(24, 24, 27, 0.45), 0 2px 4px rgba(0, 0, 0, 0.22), inset 0 1px 0 rgba(244, 244, 245, 0.22), inset 0 -1px 0 rgba(0, 0, 0, 0.35)',
  textShadow: '0 1px 0 rgba(0, 0, 0, 0.55)',
  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
  '&:hover': {
    borderColor: '#FFFFFF',
    boxShadow:
      '0 0 14px rgba(244, 244, 245, 0.25), 0 3px 6px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(244, 244, 245, 0.28), inset 0 -1px 0 rgba(0, 0, 0, 0.3)',
  },
  '& .MuiChip-label': {
    px: 1,
    py: 0,
  },
};

export const intent20ChipSx = {
  height: INTENT_CHIP_H,
  borderRadius: `${INTENT_CHIP_H / 2}px`,
  maxHeight: INTENT_CHIP_H,
  ml: 2,
  fontSize: '0.62rem',
  fontWeight: 800,
  letterSpacing: '0.04em',
  color: '#faf7ff',
  cursor: 'default',
  border: '2px solid rgba(255, 255, 255, 0.88)',
  background: 'linear-gradient(145deg, #c4b5fd 0%, #8b5cf6 28%, #6366f1 55%, #4f46e5 78%, #7c3aed 100%)',
  boxShadow:
    '0 0 12px rgba(139, 92, 246, 0.5), 0 2px 5px rgba(79, 70, 229, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.55), inset 0 -1px 0 rgba(0, 0, 0, 0.14)',
  textShadow: '0 1px 2px rgba(0, 0, 0, 0.28)',
  transition: 'box-shadow 0.2s ease',
  '&:hover': {
    boxShadow:
      '0 0 16px rgba(167, 139, 250, 0.6), 0 3px 8px rgba(99, 102, 241, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.6), inset 0 -1px 0 rgba(0, 0, 0, 0.12)',
  },
  '& .MuiChip-label': {
    px: 1,
    py: 0,
  },
};

/** @returns {{ label: string, tooltip: string, sx: object } | null} */
export function intentPipelineBadgeForMessage(message) {
  if (!messageUsesIntent20(message)) return null;
  if (messageUsesIntent30(message)) {
    return {
      label: '3.0',
      tooltip: 'Intent 3.0 — TypeSafe targeting, medical history, and disambiguation',
      sx: intent30ChipSx,
    };
  }
  const settings = messageSettings(message);
  if (settings?.betaIntentUnavailableReason === 'patient_not_v2') {
    const requested = Number(settings.requestedBetaIntentVersion);
    const selected = Number.isInteger(requested) && requested > 0
      ? `Intent ${requested}.0`
      : 'The beta intent system';
    return {
      label: '2.0',
      tooltip: `${selected} was selected for this plan, but this patient has no v2 fields, so this turn used Intent 2.0`,
      sx: intent20ChipSx,
    };
  }
  return {
    label: '2.0',
    tooltip: 'Intent 2.0 — embedding-based intent pipeline',
    sx: intent20ChipSx,
  };
}

export function intentEmbeddingTimingUsesIntent30(detail) {
  if (!detail || typeof detail !== 'object') return false;
  if (detail.intentPipelineVersion === 3) return true;
  if (detail.postIntentDisambiguationSource === 'jev') return true;
  return false;
}
